import { useEffect, useCallback } from "react";
import { wsClient, type WsPriceMessage, type WsMessage } from "@/backend/ws-client";
import { usePriceStore } from "@/backend/store";
import { useMarkets } from "@/domains/markets";
import { PerpMarketProgram } from "@/solana/programs/perp-market";
import { debug } from "@/lib/debug";

const USE_WEBSOCKET = import.meta.env.VITE_USE_WEBSOCKET !== "false";

/**
 * Hook to sync mark prices from backend WebSocket.
 * Subscribes to /ws/price/:symbol for each active market.
 * 
 * Mark prices come from the configured provider (CoinAPI/Massive)
 * and are used for:
 * - Trading (order execution)
 * - Charts and price display
 * - PnL calculations
 * - Liquidation checks
 * - TP/SL triggers
 */
export const useMarkPriceSync = () => {
  const { markets } = useMarkets();
  const setPrice = usePriceStore.getState().setPrice; // Updates marketPrices

  const handlePriceMessage = useCallback((symbol: string) => {
    return (message: WsMessage) => {
      if (message.type !== "price") return;
      
      const priceMsg = message as WsPriceMessage;
      const price = parseFloat(priceMsg.price);
      
      if (isNaN(price) || price <= 0) return;

      // Update marketPrices in store (triggers re-renders for subscribed components)
      setPrice(symbol, price);
      
      // Update PerpMarketProgram with backend's 24H change data
      if (priceMsg.price_change_24h && priceMsg.price_change_percent_24h) {
        PerpMarketProgram.updateMarketStats(symbol, {
          currentPrice: price,
          priceChange24h: parseFloat(priceMsg.price_change_24h),
          priceChangePercent24h: parseFloat(priceMsg.price_change_percent_24h),
        });
      }
    };
  }, [setPrice]);

  useEffect(() => {
    if (!USE_WEBSOCKET || markets.length === 0) return;

    debug.log('MarkPriceSync', `Subscribing to ${markets.length} mark price channels`);
    
    // Subscribe to each market's individual price channel
    const unsubscribers = markets.map(market => {
      const channel = `price/${market.symbol}`;
      return wsClient.subscribe(channel, handlePriceMessage(market.symbol));
    });

    return () => {
      debug.log('MarkPriceSync', 'Unsubscribing from mark price channels');
      unsubscribers.forEach(unsub => unsub());
    };
  }, [markets, handlePriceMessage]);

  return {
    isConnected: markets.length > 0,
  };
};

